export const INSIGHTS_ROLE_NAME = 'insights';

/** Single place for post-login / guard redirect target. */
export const INSIGHTS_DEFAULT_ROUTE = '/manager-dashboard/dashboard';

/**
 * Paths insights-only users may hit (auth + error). Exact match only — avoids e.g. /login/evil matching /login.
 */
const INSIGHTS_EXACT_ALLOWED_PATHS = new Set([
  '/login',
  '/forgot-password',
  '/error-page',
]);

function resolveRoleName(user) {
  if (!user) {
    return null;
  }
  if (user.role_name != null && user.role_name !== '') {
    return String(user.role_name);
  }
  const role = user.role;
  if (role == null) {
    return null;
  }
  if (typeof role === 'string') {
    return role;
  }
  if (typeof role === 'object' && role.name != null && role.name !== '') {
    return String(role.name);
  }
  return null;
}

/**
 * Normalize pathname from the location API for comparisons.
 * Returns null if the path is unusable (bad encoding, traversal).
 */
function pathHasParentTraversal(path) {
  return path.split('/').some(segment => segment === '..');
}

export function normalizePathnameForInsightsGuard(pathname) {
  if (pathname == null || typeof pathname !== 'string') {
    return null;
  }
  let path = pathname.split('?')[0].split('#')[0];
  if (pathHasParentTraversal(path)) {
    return null;
  }
  try {
    path = decodeURI(path);
  }
  catch (e) {
    return null;
  }
  if (pathHasParentTraversal(path)) {
    return null;
  }
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }
  path = path.replace(/\/{2,}/g, '/');
  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  return path;
}

export function userHasInsightsOnlyRole(user) {
  const name = resolveRoleName(user);
  if (name == null || name === '') {
    return false;
  }
  return name.toLowerCase() === INSIGHTS_ROLE_NAME;
}

/**
 * Session-scoped mirror of userHasInsightsOnlyRole for the *current* session.
 *
 * The legacy login bootstrap fires company/user/role/notification requests from DAOs that have no
 * user in hand, and it does so before dataModel.usersDAO.currentUser is populated. The API answers
 * those with 403 for insights-only users (see InsightsRoleAccess::ALLOWED_ROUTES on the API side),
 * so the DAOs need a role signal that is available the moment login resolves. Both entry points
 * (SessionManager.login and DataModel.initializeAlreadyLoggedIn) set this as soon as they have the
 * user; logoff clears it. Module state, not localStorage: it must not outlive the tab.
 */
let insightsOnlySession = false;

export function setInsightsOnlySessionFromUser(user) {
  insightsOnlySession = userHasInsightsOnlyRole(user);
  return insightsOnlySession;
}

export function clearInsightsOnlySession() {
  insightsOnlySession = false;
}

export function isInsightsOnlySession() {
  return insightsOnlySession;
}

export function isPathAllowedForInsightsUser(pathname) {
  const path = normalizePathnameForInsightsGuard(pathname);
  if (path == null) {
    return false;
  }
  if (INSIGHTS_EXACT_ALLOWED_PATHS.has(path)) {
    return true;
  }
  if (path.startsWith('/set-password/')) {
    return true;
  }
  if (path === INSIGHTS_DEFAULT_ROUTE || path.startsWith(`${INSIGHTS_DEFAULT_ROUTE}/`)) {
    return true;
  }
  return false;
}

/**
 * If an insights-only user must be sent to the dashboard, returns the target path; otherwise null.
 * Keeps redirect logic out of the component for easier testing and one source of truth.
 */
export function getInsightsRouteRedirectTarget({ needsLogin, currentUser, pathname }) {
  if (needsLogin || !currentUser) {
    return null;
  }
  if (!userHasInsightsOnlyRole(currentUser)) {
    return null;
  }
  if (isPathAllowedForInsightsUser(pathname)) {
    return null;
  }
  return INSIGHTS_DEFAULT_ROUTE;
}



// WEBPACK FOOTER //
// ./src/helpers/user/insightsRoleAccess.js